import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { initNativeImageCache } from "@/lib/nativeImageCache";
import { initGlobalHaptics } from "@/lib/globalHaptics";
import { hydrateCache } from "@/lib/swr";
import { startOfflineSync } from "@/lib/offlineSync";

const storageReady = Promise.all([initNativeImageCache(), hydrateCache()]);
initGlobalHaptics();

// Belt-and-braces: block multi-touch pinch gestures even if the WebView
// ignores `user-scalable=no` (observed on iOS 17+ WKWebView).
["gesturestart", "gesturechange", "gestureend"].forEach((ev) =>
  document.addEventListener(ev, (e) => e.preventDefault(), { passive: false })
);
document.addEventListener("touchmove", (e) => { if (e.touches.length > 1) e.preventDefault(); }, { passive: false });
document.addEventListener("dblclick", (e) => e.preventDefault(), { passive: false });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
const mount = () => root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
Promise.race([storageReady, new Promise((r) => setTimeout(r, 1500))]).finally(() => {
  mount();
  startOfflineSync();
});

// Register the media-cache Service Worker. Caches /api/media/* and image
// bytes on the device so repeat opens are INSTANT even on slow 4G.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        reg.addEventListener("updatefound", () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener("statechange", () => {
            if (sw.state === "installed" && navigator.serviceWorker.controller) {
              // New shell is ready — activate it right away without reloading.
              sw.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch((err) => console.warn("SW registration failed:", err));
  });
}
